import { gql, useMutation } from '@apollo/client'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useCallback, useContext, useRef, useState } from 'react'
import { DataroomFile, Stage } from 'utils/__generated__/graphql'

import { DATAROOM_FRAGMENT } from '../../constants'
import { getIconByMimetype } from '../FileIcons'
import { FILE_FRAGMENT, STAGE_FRAGMENT } from '../constants'
import DataroomContext, { DataroomContextType } from '../context'
import Delete from './Delete'
import Edit from './Edit'

const UPDATE_STAGE = gql`
  ${DATAROOM_FRAGMENT}
  ${STAGE_FRAGMENT}
  mutation UPDATE_STAGE($dataroomId: ID!, $stage: StageInput!) {
    updateStage(dataroomId: $dataroomId, stage: $stage) {
      ...DataroomFragment
      stages {
        ...StageFragment
      }
    }
  }
`

const UPLOAD_STAGE_FILE = gql`
  ${FILE_FRAGMENT}
  mutation UPLOAD_STAGE_FILE($file: Upload!) {
    file: uploadStageFile(file: $file) {
      ...FileFragment
    }
  }
`

type Order = 'asc' | 'desc'

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1
  if (b[orderBy] > a[orderBy]) return 1
  return 0
}

function getComparator<Key extends keyof never>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

interface HeadCell {
  id: string
  label: string
  sort: boolean
}

const headCells: readonly HeadCell[] = [
  { id: 'expand', label: '', sort: false },
  { id: 'name', label: 'Name', sort: true },
  { id: 'timestamp', label: 'Timestamp', sort: true },
  { id: 'notes', label: 'Notes', sort: true },
  { id: 'actions', label: '', sort: false },
]

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  order: Order
  orderBy: string
}

function EnhancedTableHead({
  order,
  orderBy,
  onRequestSort,
}: EnhancedTableProps) {
  const theme = useTheme()

  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              ...(headCell.id === 'expand' && { width: 48, p: 0 }),
            }}
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

function FileRow({
  file,
  onDeleteFile,
}: {
  file: DataroomFile
  onDeleteFile: (file: DataroomFile) => void
}) {
  const theme = useTheme()
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Avatar
          sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}
        >
          {getIconByMimetype(file.mimetype || '')}
        </Avatar>
        <Stack>
          <Typography variant="body2">{file.filename}</Typography>
          <Typography variant="caption" color="text.secondary">
            {file.mimetype}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Tooltip title="Download" placement="top">
          <IconButton
            size="small"
            component="a"
            href={file.url || ''}
            download
            rel="noopener noreferrer"
          >
            <CloudDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete file" placement="top">
          <IconButton size="small" onClick={() => setConfirmOpen(true)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Delete File</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <b>{file.filename}</b>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disableElevation
              onClick={() => {
                setConfirmOpen(false)
                onDeleteFile(file)
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Stack>
  )
}

function StageRow({
  stage,
  open,
  onToggle,
}: {
  stage: Stage
  open: boolean
  onToggle: () => void
}) {
  const { dataroom } = useContext<DataroomContextType>(DataroomContext)
  const { enqueueSnackbar } = useSnackbar()
  const [updateStage] = useMutation(UPDATE_STAGE)
  const [uploadStageFile] = useMutation(UPLOAD_STAGE_FILE)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadProgress, setUploadProgress] = useState<{
    current: number
    total: number
  } | null>(null)

  const files = stage.files || []

  const handleFilesSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files
      if (!selected?.length) return

      const total = selected.length
      const uploaded: { filename: string; url: string; mimetype: string }[] = []

      setUploadProgress({ current: 0, total })

      for (let i = 0; i < total; i++) {
        try {
          const { data } = await uploadStageFile({
            variables: { file: selected[i] },
          })
          if (data?.file) uploaded.push(data.file)
          setUploadProgress({ current: i + 1, total })
        } catch {
          enqueueSnackbar(`Failed to upload ${selected[i].name}`, {
            variant: 'error',
            autoHideDuration: 3000,
          })
        }
      }

      setUploadProgress(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (!uploaded.length) return

      const existing = files.map(f => ({
        filename: f?.filename,
        url: f?.url,
        mimetype: f?.mimetype,
        filesize: f?.filesize,
      }))

      const added = uploaded.map(f => ({
        filename: f.filename,
        url: f.url,
        mimetype: f.mimetype,
      }))

      updateStage({
        variables: {
          dataroomId: dataroom.id,
          stage: { id: stage.id, files: [...existing, ...added] },
        },
        onCompleted: () =>
          enqueueSnackbar(
            `${added.length} file${added.length > 1 ? 's' : ''} added`,
            { variant: 'success', autoHideDuration: 3000 }
          ),
        onError: () =>
          enqueueSnackbar('Failed to update stage', {
            variant: 'error',
            autoHideDuration: 3000,
          }),
      })
    },
    [
      dataroom.id,
      stage.id,
      files,
      uploadStageFile,
      updateStage,
      enqueueSnackbar,
    ]
  )

  const handleDeleteFile = useCallback(
    (file: DataroomFile) => {
      const remaining = files
        .filter(f => f && f.url !== file.url)
        .map(f => ({
          filename: f?.filename,
          url: f?.url,
          mimetype: f?.mimetype,
          filesize: f?.filesize,
        }))

      updateStage({
        variables: {
          dataroomId: dataroom.id,
          stage: { id: stage.id, files: remaining },
        },
        onCompleted: () =>
          enqueueSnackbar('File deleted', {
            variant: 'success',
            autoHideDuration: 3000,
          }),
        onError: () =>
          enqueueSnackbar('Failed to delete file', {
            variant: 'error',
            autoHideDuration: 3000,
          }),
      })
    },
    [dataroom.id, stage.id, files, updateStage, enqueueSnackbar]
  )

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell sx={{ width: 48, p: 0 }}>
          {files.length > 0 && (
            <IconButton size="small" onClick={onToggle}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{stage.name}</TableCell>
        <TableCell>
          {stage.timestamp
            ? format(new Date(stage.timestamp), 'MMM d, yyyy h:mm a')
            : ''}
        </TableCell>
        <TableCell>{stage.notes}</TableCell>
        <TableCell>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
            spacing={1}
          >
            {files.length > 0 && (
              <Chip label={`${files.length} files`} size="small" />
            )}
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={
                uploadProgress ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <NoteAddIcon />
                )
              }
              disabled={!!uploadProgress}
            >
              {uploadProgress
                ? `Adding ${uploadProgress.current}/${uploadProgress.total}`
                : 'Add Files'}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleFilesSelected}
              />
            </Button>
            <Edit stage={stage} />
            <Delete stage={stage} />
          </Stack>
        </TableCell>
      </TableRow>
      {files.length > 0 && (
        <TableRow>
          <TableCell sx={{ py: 0 }} colSpan={headCells.length}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1, px: 2 }}>
                <Stack spacing={0.5} divider={<Divider />}>
                  {files.map(file => {
                    if (!file) return null
                    return (
                      <FileRow
                        key={file.url}
                        file={file}
                        onDeleteFile={handleDeleteFile}
                      />
                    )
                  })}
                </Stack>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function StagesTable() {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState('name')
  const { dataroom } = useContext<DataroomContextType>(DataroomContext)

  const [openStages, setOpenStages] = useState(new Set<string>())

  const toggleStage = useCallback((stageId: string) => {
    setOpenStages(prev => {
      const next = new Set(prev)
      if (next.has(stageId)) next.delete(stageId)
      else next.add(stageId)
      return next
    })
  }, [])

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const stages = (dataroom.stages ?? [])
    .filter((s): s is Stage => !!s)
    .map(stage => ({
      ...stage,
      _sort_name: stage.name || '',
      _sort_timestamp: stage.timestamp || '',
      _sort_notes: stage.notes || '',
    }))
    .sort(
      getComparator(
        order,
        orderBy === 'name'
          ? '_sort_name'
          : orderBy === 'timestamp'
          ? '_sort_timestamp'
          : orderBy === 'notes'
          ? '_sort_notes'
          : '_sort_name'
      )
    )

  return (
    <TableContainer
      component={props => <Paper {...props} elevation={0} variant="outlined" />}
    >
      <Table aria-label="collapsible table">
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {stages.map(stage => (
            <StageRow
              key={stage.id}
              stage={stage}
              open={openStages.has(stage.id ?? '')}
              onToggle={() => toggleStage(stage.id ?? '')}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default StagesTable
